import { Link } from '@tanstack/react-router'
import { StatusPill } from '@/components/status-pill'
import type { StatusTone } from '@/lib/status-tones'
import { useProjectSourceQuote } from './api'

/** "Oct 22", with the year when it is not this year. */
function shortDate(value: string) {
  const date = new Date(value)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  })
}

/**
 * The quote the project was started from, under the customer: its link, whether the customer
 * signed and paid, and when. Nothing for projects that did not start from a quote.
 */
export function ProjectQuoteSummary({ projectUuid }: { projectUuid: string }) {
  const { data } = useProjectSourceQuote(projectUuid)
  if (!data) return null
  const total = Number(data.total)
  const paid = Number(data.paid)
  const payment: { tone: StatusTone; label: string } =
    paid <= 0
      ? { tone: 'neutral', label: 'Not paid' }
      : paid >= total
        ? { tone: 'success', label: 'Paid' }
        : { tone: 'warning', label: 'Partly paid' }
  const dates = [
    `Created ${shortDate(data.created_at)}`,
    data.signature ? `Signed ${shortDate(data.signature.signed_at)}` : null,
    data.paid_at ? `Paid ${shortDate(data.paid_at)}` : null,
  ].filter(Boolean)

  return (
    <div className="space-y-1 border-t pt-3" data-project-quote={data.quote_id}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Link
          className="text-sm font-medium text-primary hover:underline"
          params={{ quoteId: data.quote_id, stage: 'invoicing' }}
          title={data.title}
          to="/quotes/$quoteId/workflow/$stage"
        >
          Quote {data.quote_number}
        </Link>
        <StatusPill tone={data.signature ? 'success' : 'neutral'}>
          {data.signature ? 'Signed' : 'Not signed'}
        </StatusPill>
        <StatusPill tone={payment.tone}>{payment.label}</StatusPill>
      </div>
      <p className="text-xs text-muted-foreground">{dates.join(' · ')}</p>
    </div>
  )
}
